import { Injectable, TemplateRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { FullScreenMapComponent } from '@features/map/full-screen-map/full-screen-map.component';
import {
  AutoZoomFitParams,
  FullScreenMapProps,
  MapMarker,
  MapInput,
  MarkerClusterContentTable,
} from '@features/map/interfaces';
import MarkerPopupUtils from '@features/map/utils/marker-popup.utils';
import DateUtils from '@shared/utils/date.utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { AssetDto } from '@pages/assets-page/components/interfaces';
import { DialogService } from '@core/services/dialog.service';
import { DvDialogComponent } from '@devinco/ui/dds';
import { MapId } from '@features/map/constants/map-id.enum';
import { HardwareDeviceStatus } from '@shared/constants/hardware-device-status';
import TimedMapMarkerUtils from '@shared/components/timed-marker-map-menu-panel/timed-marker.utils';

@Injectable()
export class AssetsMapService {
  private dialog = inject(DialogService);
  private translate = inject(TranslateService);

  showAssetsOnMap(
    markers$: Observable<MapInput[]>,
    menuPanelTemplate: TemplateRef<TimedMarkerMapMenuPanelComponent>,
    autoZoomFit$?: BehaviorSubject<AutoZoomFitParams>,
    toggleMarkerClustering$?: BehaviorSubject<boolean>
  ): DvDialogComponent<FullScreenMapProps, FullScreenMapComponent> {
    const data: FullScreenMapProps = {
      id: MapId.AssetsMap,
      markers$: markers$,
      autoZoomFit$,
      toggleMarkerClustering$,
      fitBoundsAfterRefresh: false,
      useMarkerClustering: true,
      customTemplate: {
        template: menuPanelTemplate,
        isFullScreenTemplate: true,
      },
    };

    return this.dialog.open<FullScreenMapProps, FullScreenMapComponent>({
      size: 'full',
      contentComponent: FullScreenMapComponent,
      hideHeader: true,
      hideFooter: true,
      data,
    });
  }

  getMapMarkers(assets: AssetDto[]): MapInput[] {
    const points: MapMarker[] = assets
      .filter(
        asset =>
          asset.LastGPSLat &&
          asset.LastGPSLon &&
          asset.FindActive === HardwareDeviceStatus.Active
      )
      .map(asset => {
        const timeDifference = DateUtils.getTimeDifferenceInMinutes(
          asset.LastActivityTime!,
          DateUtils.getCurrentNOTime()
        );

        const assetIconUrl = TimedMapMarkerUtils.getEntityIconByTimeDifference(
          timeDifference,
          'Asset'
        );
        return {
          lat: asset.LastGPSLat!,
          lng: asset.LastGPSLon!,
          options: {
            riseOnHover: true,
            tooltipValue: asset.Description || '',
            isToolTipPermanent: true,
            customMarker: {
              iconUrl: assetIconUrl,
              iconSize: [40, 40],
            },
            popup: {
              customElement:
                MarkerPopupUtils.getAssetPopupElementFromRegistry(asset),
              popupTableData: {
                ...this.constructClusterPopupTable(asset, assetIconUrl),
                iconHeader: this.translate.instant('General.Icon'),
              },
            },
          },
        };
      });

    return [{ points }];
  }

  private constructClusterPopupTable(
    asset: AssetDto,
    iconUrl: string
  ): MarkerClusterContentTable {
    const popupData: { [header: string]: string } = {};
    popupData[this.translate.instant('General.Icon')] = iconUrl;
    popupData[this.translate.instant('AssetPopup.Description')] =
      asset.Description || '';
    popupData[this.translate.instant('AssetPopup.AssetOwner')] =
      asset.Username || '';
    popupData[this.translate.instant('AssetPopup.Department')] =
      asset.Department || '';
    popupData[this.translate.instant('AssetPopup.CurrentLocation')] =
      asset.CurrentLocation || '';
    popupData[this.translate.instant('AssetPopup.FindSerial')] =
      String(asset.Serial) || '';
    popupData[this.translate.instant('AssetPopup.LastActivity')] =
      asset.LastActivityTime
        ? DateUtils.convertDateTimeToNO(new Date(asset.LastActivityTime))
        : '';
    popupData[this.translate.instant('AssetPopup.LastMoved')] = asset.LastMoved
      ? DateUtils.convertDateTimeToNO(new Date(asset.LastMoved))
      : '';
    popupData[this.translate.instant('AssetPopup.Temperature')] =
      asset.Temperature ? String(asset.Temperature) : '';
    return {
      contentData: popupData,
    };
  }
}
